import React, { useRef } from 'react';
import { CircleDashed, AlertTriangle } from 'lucide-react';
import { useSecondScreenData } from '../../../../contexts/SecondScreenContext';
import type { TabletopAdjudicatorDeadlineView } from '../../../../types/second-screen.types';

function formatCountdown(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
}

// adjudicatorDeadlines carries no stable id — label+atSimMs+consequence is
// the closest thing to one. Two distinct deadlines colliding on all three
// would be indistinguishable in the UI anyway, so this is as unique as it's
// worth making without the backend adding a real id.
function deadlineKey(d: TabletopAdjudicatorDeadlineView): string {
  return `${d.label}::${d.atSimMs}::${d.consequence}`;
}

interface OpenDecisionsProps {
  simTimeMs: number;
}

// Open branches (plan 01) and adjudicator deadlines (plan 04) are rendered
// as two separate lists rather than one merged per-decision countdown —
// adjudicatorDeadlines carries no branchId/actId, so there is no honest way
// to attach a specific deadline to a specific open decision, despite what
// the plan 06 mockup's layout implies.
//
// Deadlines are remembered client-side (everSeenRef) rather than trusted to
// stay in the packet once expired — the adjudicator's next authoring pass
// could drop a resolved entry, and "flips to UNRESOLVED and stays visible"
// (plan 06 acceptance criteria) needs to hold regardless of whether the
// backend keeps it around.
const OpenDecisions: React.FC<OpenDecisionsProps> = ({ simTimeMs }) => {
  const { state } = useSecondScreenData();
  const tabletop = state?.tabletop;
  const openDecisions = tabletop?.openDecisions ?? [];
  const liveDeadlines = tabletop?.adjudicatorDeadlines ?? [];

  const everSeenRef = useRef<Map<string, TabletopAdjudicatorDeadlineView>>(new Map());
  for (const d of liveDeadlines) {
    const key = deadlineKey(d);
    if (!everSeenRef.current.has(key)) everSeenRef.current.set(key, d);
  }
  const allDeadlines = Array.from(everSeenRef.current.values()).sort((a, b) => a.atSimMs - b.atSimMs);

  if (openDecisions.length === 0 && allDeadlines.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-white/30" style={{ fontSize: '16px' }}>
        No open decisions
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full gap-2 min-h-0 overflow-y-auto">
      {openDecisions.map((d) => (
        <div key={d.branchId} className="flex items-start gap-1.5 flex-shrink-0">
          <CircleDashed size={16} className="mt-0.5 flex-shrink-0 text-amber-400" />
          <p className="text-white/90 leading-snug" style={{ fontSize: '22px' }}>{d.prompt}</p>
        </div>
      ))}
      {allDeadlines.map((d) => {
        const remaining = d.atSimMs - simTimeMs;
        const expired = remaining <= 0;
        const urgent = !expired && remaining < 120_000;
        return (
          <div key={deadlineKey(d)} className="flex items-start gap-1.5 flex-shrink-0">
            <AlertTriangle size={16} className={`mt-0.5 flex-shrink-0 ${expired ? 'text-white/30' : urgent ? 'text-red-400' : 'text-amber-400'}`} />
            <div className="flex-1 min-w-0">
              <p className="text-white/90 leading-snug" style={{ fontSize: '22px' }}>{d.label}</p>
              <p
                className={`font-mono font-bold tabular-nums ${
                  expired ? 'text-white/40' : urgent ? 'text-red-400 animate-pulse' : 'text-white/70'
                }`}
                style={{ fontSize: '32px' }}
              >
                {expired ? 'UNRESOLVED' : formatCountdown(remaining)}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default OpenDecisions;
